import { useEffect, useState } from 'react';
import {
  AlertTriangle,
  BrainCircuit,
  ChevronDown,
  ChevronRight,
  Coins,
  Database,
  Loader2,
  RefreshCw,
  RotateCcw,
} from 'lucide-react';
import { EmptyState } from '../../components/EmptyState';
import { Select } from '../../components/ui/Select';
import { labApi } from '../../lib/api';

interface CostSummary {
  calls: number;
  knownCostCalls: number;
  unknownCostCalls: number;
  estimatedCostUsd: number;
  wastedCostUsd: number;
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  outputTextTokens: number;
  totalTokens: number;
  retryEvents: number;
  fallbackCalls: number;
  rejectedCalls: number;
  errorCalls: number;
  toolCalls: number;
  cacheHitRate: number;
}

interface CostAsset {
  tenantId: string;
  tenantName: string;
  labProductId: string;
  productName: string;
  targetId: string | null;
  pipeline: string;
  calls: number;
  estimatedCostUsd: number;
  wastedCostUsd: number;
  unknownCostCalls: number;
  totalTokens: number;
  reasoningTokens: number;
  retryEvents: number;
  fallbackCalls: number;
  rejectedCalls: number;
  errorCalls: number;
  firstCallAt: string;
  lastCallAt: string;
}

interface CostModel {
  provider: string;
  model: string;
  reasoningEffort: string | null;
  calls: number;
  estimatedCostUsd: number;
  unknownCostCalls: number;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  retryEvents: number;
}

interface CostReport {
  period: { days: number; dateFrom: string; dateTo: string };
  summary: CostSummary;
  assets: CostAsset[];
  models: CostModel[];
}

interface CostCall {
  id: string;
  runId: string;
  fieldName: string;
  action: string;
  phase: string;
  attempt: number;
  maxAttempts: number;
  provider: string;
  model: string;
  reasoningEffort: string | null;
  maxOutputTokens: number | null;
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  outputTextTokens: number;
  totalTokens: number;
  durationMs: number;
  providerRetryCount: number;
  toolCallCount: number;
  status: string;
  finishReason: string | null;
  fallbackUsed: boolean;
  responseId: string | null;
  serviceTier: string | null;
  estimatedCostUsd: number | null;
  pricingSource: string | null;
  createdAt: string;
}

const usdFormatter = new Intl.NumberFormat('pl-PL', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

const numberFormatter = new Intl.NumberFormat('pl-PL');

function formatUsd(value: number | null): string {
  return value == null ? 'brak wyceny' : usdFormatter.format(value);
}

function formatDate(value: string): string {
  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function statusLabel(status: string): string {
  if (status === 'success') return 'zaakceptowane';
  if (status === 'rejected_output') return 'odrzucony output';
  if (status === 'error') return 'błąd';
  return status;
}

export function LabAiCostsPanel() {
  const [days, setDays] = useState('7');
  const [report, setReport] = useState<CostReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [details, setDetails] = useState<Record<string, CostCall[]>>({});
  const [detailsLoading, setDetailsLoading] = useState<string | null>(null);

  const loadReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await labApi.getAiCostReport({ days: Number(days), limit: 50 });
      setReport(response.data.data);
    } catch (requestError) {
      const message = (requestError as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(message || 'Nie udało się pobrać telemetrii kosztów AI.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setExpandedKey(null);
    setDetails({});
    void loadReport();
  }, [days]);

  const toggleDetails = async (asset: CostAsset) => {
    if (!asset.targetId) return;
    const key = `${asset.targetId}:${asset.pipeline}`;
    if (expandedKey === key) {
      setExpandedKey(null);
      return;
    }
    setExpandedKey(key);
    if (details[key]) return;

    try {
      setDetailsLoading(key);
      const response = await labApi.getAiCostDetails({
        targetId: asset.targetId,
        pipeline: asset.pipeline,
        days: Number(days),
      });
      setDetails(previous => ({ ...previous, [key]: response.data.data }));
    } catch {
      setDetails(previous => ({ ...previous, [key]: [] }));
    } finally {
      setDetailsLoading(null);
    }
  };

  if (loading && !report) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-accent-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-xl border border-accent-primary/20 bg-accent-primary/5 p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-sm font-semibold text-text-primary">Rzeczywiste wywołania AI w Laboratorium</h2>
          <p className="mt-1 max-w-3xl text-xs leading-relaxed text-text-secondary">
            Każdy wiersz obejmuje wszystkie próby, retry, fallbacki i odpowiedzi odrzucone przez kontrolę długości. Koszt jest estymowany tylko dla modeli z rozpoznanym cennikiem.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onChange={event => setDays(event.target.value)} aria-label="Zakres raportu kosztów AI" className="min-w-[150px]">
            <option value="1">Ostatnie 24 godziny</option>
            <option value="7">Ostatnie 7 dni</option>
            <option value="30">Ostatnie 30 dni</option>
            <option value="90">Ostatnie 90 dni</option>
          </Select>
          <button type="button" onClick={() => void loadReport()} disabled={loading} className="btn-secondary inline-flex items-center gap-2 text-xs">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Odśwież
          </button>
        </div>
      </div>

      {error && (
        <div role="alert" className="flex items-center gap-2 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {report && report.summary.calls > 0 ? (
        <>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              icon={Coins}
              label="Koszt oszacowany"
              value={formatUsd(report.summary.estimatedCostUsd)}
              detail={`${report.summary.knownCostCalls}/${report.summary.calls} wywołań wycenionych`}
            />
            <MetricCard
              icon={RotateCcw}
              label="Wywołania i ponowienia"
              value={numberFormatter.format(report.summary.calls)}
              detail={`${numberFormatter.format(report.summary.retryEvents)} retry, ${report.summary.fallbackCalls} fallback`}
            />
            <MetricCard
              icon={BrainCircuit}
              label="Tokeny reasoning"
              value={numberFormatter.format(report.summary.reasoningTokens)}
              detail={`${numberFormatter.format(report.summary.outputTextTokens)} tokenów tekstu`}
            />
            <MetricCard
              icon={AlertTriangle}
              label="Koszt odrzucony"
              value={formatUsd(report.summary.wastedCostUsd)}
              detail={`${report.summary.rejectedCalls} odrzuconych, ${report.summary.errorCalls} błędów`}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <SmallMetric label="Input" value={numberFormatter.format(report.summary.inputTokens)} />
            <SmallMetric label="Cache hit" value={`${(report.summary.cacheHitRate * 100).toFixed(1)}%`} />
            <SmallMetric label="Cache write" value={numberFormatter.format(report.summary.cacheWriteInputTokens)} />
            <SmallMetric label="Web search" value={numberFormatter.format(report.summary.toolCalls)} />
            <SmallMetric label="Wywołania bez wyceny" value={numberFormatter.format(report.summary.unknownCostCalls)} />
          </div>

          <section className="overflow-hidden rounded-xl border border-border-subtle bg-surface-raised/50">
            <div className="border-b border-border-subtle px-5 py-4">
              <h3 className="text-sm font-semibold text-text-primary">Koszt według wygenerowanego zasobu</h3>
              <p className="mt-1 text-xs text-text-muted">Kliknij wiersz, aby zobaczyć każde wywołanie providera.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1050px] text-left text-xs">
                <thead className="bg-surface-base/50 text-[10px] uppercase tracking-wide text-text-muted">
                  <tr>
                    <th className="w-9 px-3 py-3" />
                    <th className="px-3 py-3">Produkt / tenant</th>
                    <th className="px-3 py-3">Pipeline</th>
                    <th className="px-3 py-3 text-right">Koszt</th>
                    <th className="px-3 py-3 text-right">Wywołania</th>
                    <th className="px-3 py-3 text-right">Retry / fallback</th>
                    <th className="px-3 py-3 text-right">Reasoning</th>
                    <th className="px-3 py-3">Ostatnie wywołanie</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {report.assets.map(asset => {
                    const key = `${asset.targetId}:${asset.pipeline}`;
                    const expanded = expandedKey === key;
                    return (
                      <AssetRows
                        key={key}
                        asset={asset}
                        expanded={expanded}
                        calls={details[key]}
                        loading={detailsLoading === key}
                        onToggle={() => void toggleDetails(asset)}
                      />
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section className="overflow-hidden rounded-xl border border-border-subtle bg-surface-raised/50">
            <div className="border-b border-border-subtle px-5 py-4">
              <h3 className="text-sm font-semibold text-text-primary">Modele i poziomy reasoning</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[780px] text-left text-xs">
                <thead className="bg-surface-base/50 text-[10px] uppercase tracking-wide text-text-muted">
                  <tr>
                    <th className="px-4 py-3">Provider / model</th>
                    <th className="px-4 py-3">Reasoning</th>
                    <th className="px-4 py-3 text-right">Koszt</th>
                    <th className="px-4 py-3 text-right">Wywołania</th>
                    <th className="px-4 py-3 text-right">Input / output</th>
                    <th className="px-4 py-3 text-right">Tokeny reasoning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {report.models.map(model => (
                    <tr key={`${model.provider}:${model.model}:${model.reasoningEffort || 'default'}`}>
                      <td className="px-4 py-3">
                        <p className="font-medium text-text-primary">{model.model}</p>
                        <p className="text-[10px] text-text-muted">{model.provider}</p>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">{model.reasoningEffort || 'domyślny'}</td>
                      <td className="px-4 py-3 text-right font-medium text-text-primary">
                        {model.unknownCostCalls === model.calls ? 'brak wyceny' : formatUsd(model.estimatedCostUsd)}
                      </td>
                      <td className="px-4 py-3 text-right text-text-secondary">{numberFormatter.format(model.calls)}</td>
                      <td className="px-4 py-3 text-right text-text-secondary">{numberFormatter.format(model.inputTokens)} / {numberFormatter.format(model.outputTokens)}</td>
                      <td className="px-4 py-3 text-right text-text-secondary">{numberFormatter.format(model.reasoningTokens)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : !loading ? (
        <div className="rounded-xl border border-border-subtle bg-surface-raised/50">
          <EmptyState
            icon={Database}
            title="Brak nowych danych pomiarowych"
            description="Telemetria zacznie się zapełniać po wdrożeniu i uruchomieniu kolejnego generowania w Laboratorium. Historycznych kosztów nie da się wiarygodnie odtworzyć."
            size="sm"
          />
        </div>
      ) : null}
    </div>
  );
}

function MetricCard(props: {
  icon: typeof Coins;
  label: string;
  value: string;
  detail: string;
}) {
  const Icon = props.icon;
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-raised/50 p-4">
      <div className="flex items-center gap-2 text-xs text-text-muted">
        <Icon className="h-4 w-4 text-accent-primary" />
        {props.label}
      </div>
      <p className="mt-2 text-xl font-semibold text-text-primary">{props.value}</p>
      <p className="mt-1 text-[10px] text-text-muted">{props.detail}</p>
    </div>
  );
}

function SmallMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border-subtle bg-surface-base/30 px-3 py-2.5">
      <span className="text-xs text-text-muted">{label}</span>
      <span className="text-xs font-medium text-text-primary">{value}</span>
    </div>
  );
}

function AssetRows(props: {
  asset: CostAsset;
  expanded: boolean;
  calls?: CostCall[];
  loading: boolean;
  onToggle: () => void;
}) {
  const { asset, expanded, calls, loading, onToggle } = props;
  return (
    <>
      <tr className="cursor-pointer transition-colors hover:bg-surface-hover/40" onClick={onToggle}>
        <td className="px-3 py-3 text-text-muted">
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </td>
        <td className="px-3 py-3">
          <p className="font-medium text-text-primary">{asset.productName}</p>
          <p className="text-[10px] text-text-muted">{asset.tenantName}</p>
        </td>
        <td className="px-3 py-3 text-text-secondary">{asset.pipeline}</td>
        <td className="px-3 py-3 text-right">
          <p className="font-medium text-text-primary">{formatUsd(asset.estimatedCostUsd)}</p>
          {asset.unknownCostCalls > 0 && <p className="text-[10px] text-amber-400">+ {asset.unknownCostCalls} bez wyceny</p>}
          {asset.wastedCostUsd > 0 && <p className="text-[10px] text-danger">odrzucone {formatUsd(asset.wastedCostUsd)}</p>}
        </td>
        <td className="px-3 py-3 text-right text-text-secondary">{asset.calls}</td>
        <td className="px-3 py-3 text-right text-text-secondary">{asset.retryEvents} / {asset.fallbackCalls}</td>
        <td className="px-3 py-3 text-right text-text-secondary">{numberFormatter.format(asset.reasoningTokens)}</td>
        <td className="px-3 py-3 text-text-secondary">{formatDate(asset.lastCallAt)}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={8} className="bg-surface-base/30 px-5 py-4">
            {loading ? (
              <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-accent-primary" /></div>
            ) : calls && calls.length > 0 ? (
              <div className="overflow-x-auto rounded-lg border border-border-subtle">
                <table className="w-full min-w-[1150px] text-left text-[11px]">
                  <thead className="bg-surface-raised/70 text-[9px] uppercase tracking-wide text-text-muted">
                    <tr>
                      <th className="px-3 py-2">Krok / faza</th>
                      <th className="px-3 py-2">Model</th>
                      <th className="px-3 py-2">Reasoning</th>
                      <th className="px-3 py-2 text-right">Próba</th>
                      <th className="px-3 py-2 text-right">Input / cache</th>
                      <th className="px-3 py-2 text-right">Output / reasoning</th>
                      <th className="px-3 py-2 text-right">Koszt</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2 text-right">Czas</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-subtle">
                    {calls.map(call => (
                      <tr key={call.id}>
                        <td className="px-3 py-2">
                          <p className="font-medium text-text-primary">{call.fieldName}</p>
                          <p className="text-[9px] text-text-muted">{call.phase} · {call.action}</p>
                        </td>
                        <td className="px-3 py-2">
                          <p className="text-text-primary">{call.model}</p>
                          <p className="text-[9px] text-text-muted">{call.provider}</p>
                        </td>
                        <td className="px-3 py-2 text-text-secondary">{call.reasoningEffort || 'domyślny'}</td>
                        <td className="px-3 py-2 text-right text-text-secondary">{call.attempt}/{call.maxAttempts}{call.providerRetryCount > 0 ? ` +${call.providerRetryCount}` : ''}</td>
                        <td className="px-3 py-2 text-right text-text-secondary">{numberFormatter.format(call.inputTokens)} / {numberFormatter.format(call.cachedInputTokens)}</td>
                        <td className="px-3 py-2 text-right text-text-secondary">{numberFormatter.format(call.outputTokens)} / {numberFormatter.format(call.reasoningTokens)}</td>
                        <td className="px-3 py-2 text-right font-medium text-text-primary">{formatUsd(call.estimatedCostUsd)}</td>
                        <td className={`px-3 py-2 ${call.status === 'success' ? 'text-success' : 'text-danger'}`}>{statusLabel(call.status)}</td>
                        <td className="px-3 py-2 text-right text-text-secondary">{(call.durationMs / 1000).toFixed(1)} s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="py-4 text-center text-xs text-text-muted">Nie udało się pobrać szczegółów wywołań.</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}
